import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Bus, Plus, Trash2, Loader2, MapPin, Building2 } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';

interface BusRoute {
    id: number;
    area_name: string;
    added_at: string;
}

interface CityInfo {
    city_id: number;
    city_name: string;
}

const BusRoutes: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [routes, setRoutes] = useState<BusRoute[]>([]);
    const [city, setCity] = useState<CityInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [areaInput, setAreaInput] = useState('');
    const [adding, setAdding] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    useEffect(() => {
        fetchRoutes();
    }, [id]);

    const fetchRoutes = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(`/bus-routes/${id}`);
            if (response.success) {
                setRoutes(response.data.routes || []);
                setCity(response.data.city || null);
            } else {
                showToast(response.message || 'Failed to load bus routes', 'error');
            }
        } catch (err) {
            showToast('Failed to load bus routes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!areaInput.trim()) {
            showToast('Please enter an area name', 'error');
            return;
        }
        try {
            setAdding(true);
            const response = await apiRequest(`/bus-routes/${id}`, {
                method: 'POST',
                body: { area_name: areaInput.trim() },
            });
            if (response.success) {
                showToast('Bus route added!', 'success');
                setAreaInput('');
                await fetchRoutes();
            } else {
                showToast(response.message || 'Failed to add route', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to add route', 'error');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (routeId: number) => {
        if (!confirm('Remove this bus route?')) return;
        try {
            setDeletingId(routeId);
            const response = await apiRequest(`/bus-routes/${id}/${routeId}`, { method: 'DELETE' });
            if (response.success) {
                showToast('Bus route removed!', 'success');
                setRoutes(prev => prev.filter(r => r.id !== routeId));
            } else {
                showToast(response.message || 'Failed to remove route', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to remove route', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-brandColor rounded-lg text-white">
                        <Bus className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Bus Routes</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Manage school bus route areas</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
                </div>
            ) : (
                <div className="flex-1 overflow-auto p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Left — Add route */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">

                            {/* City label */}
                            {city && (
                                <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-brandColor/10 rounded-lg border border-brandColor/20">
                                    <Building2 className="w-4 h-4 text-brandColor flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">School City</p>
                                        <p className="text-sm font-semibold text-brandColor">{city.city_name}</p>
                                    </div>
                                </div>
                            )}

                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Add Area Name</h2>

                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={areaInput}
                                    onChange={(e) => setAreaInput(e.target.value)}
                                    onKeyPress={(e) => { if (e.key === 'Enter') handleAdd(); }}
                                    placeholder="e.g. Banjara Hills, Jubilee Hills..."
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent text-sm"
                                />
                                <button
                                    onClick={handleAdd}
                                    disabled={adding || !areaInput.trim()}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    {adding ? 'Adding...' : 'Add'}
                                </button>
                            </div>
                        </div>

                        {/* Right — Routes list */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Bus className="w-4 h-4 text-brandColor" />
                                Bus Routes ({routes.length})
                            </h2>

                            {routes.length === 0 ? (
                                <div className="text-center py-10">
                                    <MapPin className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No bus routes added yet</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add area names on the left to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                    {routes.map((route) => (
                                        <div
                                            key={route.id}
                                            className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <MapPin className="w-4 h-4 text-brandColor flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                        {route.area_name}
                                                    </p>
                                                    {city && (
                                                        <p className="text-xs text-gray-400">{city.city_name}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDelete(route.id)}
                                                disabled={deletingId === route.id}
                                                className="ml-2 p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                            >
                                                {deletingId === route.id
                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                    : <Trash2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default BusRoutes;
